<template>
  <!-- Teleported to body (replaces the source's createPortal into the
       PhoneFrame overlay root). `theme` (--fr-* vars) must be bound here
       because CSS custom properties don't cascade through Teleport. -->
  <Teleport to="body">
    <div v-if="open" class="ots-panel" data-overlay :style="theme">
      <!-- Header -->
      <div class="ots-header">
        <button
          id="product-detail-back"
          type="button"
          aria-label="Back"
          class="ots-back gtm-click"
          @click="emit('close')"
        >
          <ChevronLeft :size="20" />
        </button>
        <div class="ots-header__titles">
          <div class="ots-header__eyebrow">Before you redeem</div>
          <div class="ots-header__brand">{{ offer.brand }}</div>
        </div>
        <Info :size="16" class="ots-header__info" />
      </div>

      <!-- Body -->
      <div class="ots-body no-scrollbar" data-scrollable>
        <img v-if="offer.img" :src="offer.img" :alt="offer.headline" class="ots-hero" />

        <!-- Headline -->
        <div>
          <h2 class="ots-headline">{{ offer.headline }}</h2>
          <p v-if="offer.sub" class="ots-sub">{{ offer.sub }}</p>
          <div v-if="offer.expires" class="ots-expires">{{ offer.expires }}</div>
        </div>

        <!-- Blocked banner -->
        <div v-if="!availability.ok" class="ots-blocked">
          <AlertTriangle :size="16" class="ots-blocked__icon" />
          <div>
            <div class="ots-blocked__label">{{ availability.blockedLabel ?? 'Unavailable' }}</div>
            <div class="ots-blocked__reason">{{ availability.reason }}</div>
          </div>
        </div>

        <!-- How to use -->
        <div v-if="terms.howToUse">
          <div class="ots-section-label">
            <ListChecks :size="16" class="ots-accent" />
            How to use
          </div>
          <div class="ots-richtext" v-html="terms.howToUse" />
        </div>

        <!-- Description -->
        <div v-if="terms.description">
          <div class="ots-section-label">
            <Info :size="16" class="ots-accent" />
            Description
          </div>
          <div class="ots-description ots-richtext" v-html="terms.description" />
        </div>

        <!-- Frequency -->
        <div v-if="terms.frequency">
          <div class="ots-section-label">
            <Repeat :size="16" class="ots-accent" />
            How often
          </div>
          <div>
            <div class="ots-frequency__cap">
              {{ terms.frequency.cap }} {{ terms.frequency.cap === 1 ? 'use' : 'uses' }} {{ perLabel }}
            </div>
          </div>
        </div>

        <!-- Restrictions -->
        <div v-if="terms.restrictions">
          <div class="ots-section-label">
            <Info :size="16" class="ots-accent" />
            Restrictions
          </div>
          <div class="ots-richtext" v-html="terms.restrictions" />
        </div>

        <!-- Blackout dates -->
        <div v-if="terms.blackoutDates && terms.blackoutDates.length">
          <div class="ots-section-label">
            <CalendarOff :size="16" class="ots-accent" />
            Blackout dates
          </div>
          <div class="ots-blackouts">
            <span v-for="d in terms.blackoutDates" :key="d" class="ots-blackout">{{ d }}</span>
          </div>
        </div>
        <slot />
      </div>

      <!-- Footer CTA -->
      <div v-if="!hideCta" class="ots-footer">
        <button
          id="product-detail-continue"
          type="button"
          class="ots-cta gtm-click"
          :class="ctaOk ? (ctaVariant === 'muted' ? 'is-muted' : 'is-enabled') : 'is-blocked'"
          :disabled="!ctaOk"
          @click="ctaOk && emit('continue')"
        >
          <Ticket v-if="ctaOk && ctaVariant === 'muted'" :size="16" />
          {{ ctaOk ? ctaLabel : (availability.blockedLabel ?? 'Unavailable') }}
        </button>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed } from 'vue'
import { computeAvailability } from './validation/checkin'
import { ChevronLeft, Info, ListChecks, CalendarOff, Repeat, AlertTriangle, Ticket } from '@lucide/vue'

/**
 * ProductDetailSheet — themed pre-redemption "Before you redeem" panel.
 *
 * Shows restrictions, how-to-use, blackout dates, and frequency state
 * BEFORE the user actually redeems. When the offer is unavailable
 * (blackout / frequency-exceeded), the Continue CTA is disabled with an
 * inline reason.
 *
 * Pixel-perfect port of the Lovable source `_shared/OfferTermsPanel.tsx`.
 *
 * offer = {
 *   brand, headline, sub?, expires?,
 *   terms?: {
 *     description?, howToUse?: string[], restrictions?: string[],
 *     blackoutDates?: string[], isBlackoutToday?,
 *     frequency?: { cap, per: 'day'|'week'|'month'|'total', usedCount? }
 *   }
 * }
 */
const props = defineProps({
  open: { type: Boolean, default: false },
  offer: { type: Object, required: true },
  /** Style object of --fr-* CSS vars (e.g. useFrTokens().value) — required on
      teleported overlays because vars don't cascade through Teleport. */
  theme: { type: Object, default: null },
  ctaLabel: { type: String, default: 'Redeem' },
  ctaVariant: { type: String, default: 'primary' },
  bypassAvailability: { type: Boolean, default: false },
  hideCta: { type: Boolean, default: false }
})

const emit = defineEmits(['close', 'continue'])

const terms = computed(() => props.offer?.terms ?? {})
const usedCount = computed(() => terms.value.frequency?.usedCount ?? 0)

const perLabel = computed(() => {
  const f = terms.value.frequency
  if (!f || f.per === 'total') return 'total'
  const units = {
    minutes: 'minute', hours: 'hour', days: 'day', weeks: 'week',
    months: 'month', quarters: 'quarter', years: 'year'
  }
  const unit = units[f.per] || f.per
  const every = f.every || 1
  return every > 1 ? `per ${every} ${unit}s` : `per ${unit}`
})

// Availability (blackout/frequency blocking) — canonical logic shared with the
// validation set so the two never drift.
const availability = computed(() => computeAvailability(terms.value))
const ctaOk = computed(() => props.bypassAvailability || availability.value.ok)
</script>

<style lang="scss" scoped>
button {
  appearance: none;
  background: none;
  border: 0;
  padding: 0;
  margin: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;
}

.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

@keyframes ots-fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

// pointer-events-auto absolute inset-0 z-[150] flex flex-col overflow-hidden
// animate-in fade-in duration-150 (theme.surface)
.ots-panel {
  position: fixed;
  inset: 0;
  // No PhoneFrame in v4 — constrain to the pass column instead.
  margin-left: auto;
  margin-right: auto;
  width: 100%;
  max-width: var(--project-max-width, 480px);
  z-index: 2400; // above the pass layout + app chrome
  display: flex;
  flex-direction: column;
  overflow: hidden;
  pointer-events: auto;
  background: var(--fr-surface, #ffffff);
  color: var(--fr-ink, #1c1c1e);
  animation: ots-fade-in 150ms ease both;
}

// flex shrink-0 items-center gap-2 border-b border-current/10 px-4 pb-3
// paddingTop: max(env(safe-area-inset-top), 0.75rem)
.ots-header {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 8px;
  border-bottom: 1px solid color-mix(in srgb, currentColor 10%, transparent);
  padding: max(env(safe-area-inset-top), 0.75rem) 16px 12px;

  // min-w-0 flex-1
  &__titles {
    min-width: 0;
    flex: 1;
  }

  // text-[10px] font-bold uppercase tracking-wider muted
  &__eyebrow {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--fr-muted, #6b7280);
  }

  // truncate font-display text-lg font-bold leading-tight
  &__brand {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-family: var(--fr-font-display, inherit);
    font-size: 18px;
    font-weight: 700;
    line-height: 1.25;
  }

  // h-4 w-4 shrink-0 accent
  &__info {
    flex-shrink: 0;
    color: var(--fr-primary, #FF9900);
  }
}

// -ml-2 grid h-10 w-10 shrink-0 place-items-center rounded-full opacity-70
// hover:opacity-100
.ots-back {
  margin-left: -8px;
  display: grid;
  height: 40px;
  width: 40px;
  flex-shrink: 0;
  place-items: center;
  border-radius: 9999px;
  opacity: 0.7;

  &:hover {
    opacity: 1;
  }
}

// min-h-0 flex-1 overflow-y-auto px-5 py-5 space-y-5
.ots-body {
  min-height: 0;
  flex: 1;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 20px;

  > * + * {
    margin-top: 20px;
  }
}

.ots-hero {
  display: block;
  width: 100%;
  height: 180px;
  object-fit: cover;
  border-radius: 1rem;
}

// font-display text-2xl font-black leading-tight
.ots-headline {
  margin: 0;
  font-family: var(--fr-font-display, inherit);
  font-size: 24px;
  font-weight: 900;
  line-height: 1.25;
}

// mt-1 text-sm muted
.ots-sub {
  margin: 4px 0 0;
  font-size: 14px;
  line-height: 20px;
  color: var(--fr-muted, #6b7280);
}

// mt-3 inline-block text-[11px] font-bold uppercase tracking-wider muted
.ots-expires {
  margin-top: 12px;
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--fr-muted, #6b7280);
}

// flex items-start gap-2 rounded-2xl border border-current/15
// bg-current/[0.06] p-4
.ots-blocked {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  border-radius: 16px;
  border: 1px solid color-mix(in srgb, currentColor 15%, transparent);
  background: color-mix(in srgb, currentColor 6%, transparent);
  padding: 16px;

  // mt-0.5 shrink-0 accent
  &__icon {
    margin-top: 2px;
    flex-shrink: 0;
    color: var(--fr-primary, #FF9900);
  }

  // font-display text-xs font-bold uppercase tracking-wider
  &__label {
    font-family: var(--fr-font-display, inherit);
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  // text-sm muted
  &__reason {
    font-size: 14px;
    line-height: 20px;
    color: var(--fr-muted, #6b7280);
  }
}

// mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wider
// opacity-70
.ots-section-label {
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  opacity: 0.7;
}

.ots-accent {
  color: var(--fr-primary, #FF9900);
}

// space-y-2
.ots-list {
  list-style: none;
  margin: 0;
  padding: 0;

  > * + * {
    margin-top: 8px;
  }

  // flex items-start gap-3 text-base leading-snug
  &__item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    font-size: 16px;
    line-height: 1.375;
  }

  // mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-current opacity-50
  &__bullet {
    margin-top: 8px;
    height: 6px;
    width: 6px;
    flex-shrink: 0;
    border-radius: 9999px;
    background: currentColor;
    opacity: 0.5;
  }
}

// text-base leading-snug whitespace-pre-line
.ots-description {
  margin: 0;
  font-size: 16px;
  line-height: 1.375;
  white-space: pre-line;
}

.ots-richtext {
  font-size: 16px;
  line-height: 1.375;
  white-space: normal;

  :deep(p) { margin: 0 0 0.5rem; }
  :deep(p:last-child) { margin-bottom: 0; }
  :deep(ul) { list-style: disc; padding-left: 1.25rem; margin: 0.5rem 0; }
  :deep(ol) { list-style: decimal; padding-left: 1.25rem; margin: 0.5rem 0; }
  :deep(li) { margin: 0.25rem 0; }
  :deep(a) { color: var(--fr-primary, #FF9900); text-decoration: underline; word-break: break-word; }
}

.ots-frequency {
  // font-display text-base font-bold
  &__cap {
    font-family: var(--fr-font-display, inherit);
    font-size: 16px;
    line-height: 24px;
    font-weight: 700;
  }

  // mt-3 flex items-center gap-1.5

  // h-2 flex-1 rounded-full; used: bg-current opacity-80, else bg-current/15
  &__pip {
    height: 8px;
    flex: 1;
    border-radius: 9999px;
    background: color-mix(in srgb, currentColor 15%, transparent);

    &.is-used {
      background: currentColor;
      opacity: 0.8;
    }
  }
}

// flex flex-wrap gap-2
.ots-blackouts {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

// rounded-full border border-current/15 bg-current/[0.05] px-3 py-1
// text-sm font-medium
.ots-blackout {
  border-radius: 9999px;
  border: 1px solid color-mix(in srgb, currentColor 15%, transparent);
  background: color-mix(in srgb, currentColor 5%, transparent);
  padding: 4px 12px;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
}

// shrink-0 border-t border-current/10 px-5 pt-4
// paddingBottom: max(env(safe-area-inset-bottom), 1rem)
.ots-footer {
  flex-shrink: 0;
  border-top: 1px solid color-mix(in srgb, currentColor 10%, transparent);
  padding: 16px 20px max(env(safe-area-inset-bottom), 1rem);
}

// w-full rounded-full py-4 font-display text-sm font-bold uppercase
// tracking-[0.12em] transition active:scale-[0.99]
.ots-cta {
  width: 100%;
  border-radius: 9999px;
  padding: 16px 0;
  font-family: var(--fr-font-display, inherit);
  font-size: 14px;
  line-height: 20px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1), background-color 150ms cubic-bezier(0.4, 0, 0.2, 1);

  &:active {
    transform: scale(0.99);
  }

  // theme.primaryBtn
  &.is-enabled {
    background: var(--fr-primary, #FF9900);
    color: var(--fr-primary-foreground, #ffffff);
  }

  &.is-muted {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.4rem;
    background: var(--fr-bg, #f4f4f5);
    color: var(--fr-ink, #1c1c1e);
  }

  // bg-current/10 text-current opacity-50 cursor-not-allowed
  &.is-blocked {
    background: color-mix(in srgb, currentColor 10%, transparent);
    color: currentColor;
    opacity: 0.5;
    cursor: not-allowed;

    &:active {
      transform: none;
    }
  }
}
</style>
